import moment from 'moment-timezone';

/**
 * Call center hours, read from the company document.
 *
 * Twin of `functions/lib/callCenterHours.js` in the cloud functions repo: the
 * two are kept in step by hand, as `negativeOutcomes.js` already is. The
 * repos are separate, and a shared package for twenty lines would cost more
 * than it returns.
 *
 * Replaces `callCenterWorkingTime.js`, which had the table hardcoded and
 * worked it out with `new Date()` + `getDay()` +
 * `toLocaleTimeString('it-IT')` — where that `'it-IT'` was only the format
 * and the clock was the browser's.
 */

const FALLBACK_TIME_ZONE = 'Europe/Rome';

const toMinutes = (value) => {
  const [hours, minutes] = String(value).split(':');
  return Number(hours) * 60 + Number(minutes);
};

const configOf = (company) => {
  const config = company && company.callCenter;
  if (!config || !config.workingTime) return null;
  return config;
};

/**
 * Whether the company has hours at all. Without them nothing is open, and
 * nothing is closed either: the AI routing reads it that way.
 */
export const hasCallCenter = (company) => configOf(company) !== null;

/**
 * Was the call center open at that instant?
 *
 * A company with no call center configured is not a company always open: it
 * is a company whose hours we do not know, and it is treated as closed.
 * Erring towards closed shows up at once; erring towards open means phoning
 * someone on Christmas Day.
 */
export const isCallCenterOpenAt = (company, date) => {
  const config = configOf(company);
  if (!config) return false;

  const local = moment(date).tz(
    (company && company.timezone) || FALLBACK_TIME_ZONE,
  );

  // Two lists and not one: MM-DD recurrences are written once and hold
  // forever, full dates stay for Easter Monday, bridges and holidays. The old
  // list held full dates only and had stopped at 2025.
  const recurring = Array.isArray(config.recurringClosedDays)
    ? config.recurringClosedDays
    : [];
  const dated = Array.isArray(config.closedDays) ? config.closedDays : [];
  if (
    recurring.includes(local.format('MM-DD')) ||
    dated.includes(local.format('YYYY-MM-DD'))
  )
    return false;

  // ISO 8601 keys: Monday=1 … Sunday=7. Never `Date.getDay()`, which starts
  // from Sunday at 0 and matches this convention only from Monday to
  // Saturday — that is, until someone opens on a Sunday.
  const day = config.workingTime[local.isoWeekday()];
  // An explicit `open` and not a missing key: the CRM saves with merge, which
  // never deletes, so a closed day keeps its hours and `open` would be the
  // only thing holding it shut.
  if (!day || !day.open || !day.start || !day.end) return false;

  // The range is [start, end): with a 19:50 close, 19:49 is in and 19:50 is
  // out. No margin — the "a phone call takes time" rule lives in the form
  // subText, where whoever fills the hours reads it.
  const current = local.hours() * 60 + local.minutes();
  return current >= toMinutes(day.start) && current < toMinutes(day.end);
};

export default isCallCenterOpenAt;
